import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/common/AppText';
import { Briefcase, Search } from 'lucide-react-native';
import { useTheme } from '../../hooks/useTheme';
import { useLocale } from '../../hooks/useLocale';
import { HugeIcon } from '../icons/HugeIcon';
import { ShareIcon } from '../icons/ShareIcon';
import { TabbioMark } from '../common/TabbioMark';
import type { TrafficSourceIconKey } from '../../lib/cv-analytics-labels';
import { TRAFFIC_SOURCE_HUGEICONS } from './traffic-source-icons';

// Figma "Brand Color" (#5A2AFF). Intentionally distinct from tokens `colors.primary`
// (#5e29ff) so these analytics surfaces match the design pixel-for-pixel.
export const BRAND = '#5A2AFF';

/** Locale-aware integer formatter (grouping + Arabic-Indic digits when relevant). */
export function formatAnalyticsCount(value: number, locale: string): string {
  try {
    return new Intl.NumberFormat(locale).format(value);
  } catch {
    return String(value);
  }
}

/**
 * Light values are the exact Figma hex; dark values derive from theme tokens so
 * the (light-only) design stays coherent in dark mode.
 */
export function useAnalyticsPalette() {
  const { theme, isDark } = useTheme();
  return {
    text: isDark ? theme.text : '#4a4a4d',
    textStrong: isDark ? theme.text : '#1f1f26',
    muted: isDark ? theme.textTertiary : '#949399',
    chipBg: isDark ? theme.surfaceSecondary : '#f1f0f4',
    chipText: isDark ? theme.textSecondary : '#6c6b73',
    track: isDark ? 'rgba(255,255,255,0.12)' : '#e7e5f0',
    trackInactiveFill: isDark ? 'rgba(255,255,255,0.30)' : '#c9c6d6',
  };
}

// ─── Recruiter views line (under the reach metrics) ──────────────────
/**
 * One full-width line, e.g. "3 recruiter views · from 2 companies". Wraps
 * instead of truncating so long Arabic copy fits at any width.
 *
 * Plain `row` and `textAlign: 'left'` on purpose: native RTL is forced in
 * Arabic, so Yoga already mirrors the row and `left` means the start edge
 * (journey-theme TEXT_START, issue #82). Mirroring again by hand would put the
 * icon on the wrong side.
 */
export function RecruiterViewsLine({
  text,
  icon = 'briefcase',
}: {
  text: string;
  /** `search` for the "You appeared in N recruiter searches" line. */
  icon?: 'briefcase' | 'search';
}) {
  const palette = useAnalyticsPalette();
  const Icon = icon === 'search' ? Search : Briefcase;
  return (
    <View style={styles.recruiterLine}>
      <Icon size={16} color={palette.muted} strokeWidth={2} style={styles.recruiterLineIcon} />
      <Text style={[styles.recruiterLineText, { color: palette.text }]}>{text}</Text>
    </View>
  );
}

// ─── Badge glyphs (flag / traffic source) ────────────────────────────
/**
 * A country flag emoji in a badge or avatar slot. Decorative: the country is
 * named in the row's own text, so screen readers skip it.
 */
export function FlagGlyph({ flag, size = 18 }: { flag: string; size?: number }) {
  return (
    <Text
      style={[styles.flagGlyph, { fontSize: size, lineHeight: Math.round(size * 1.25) }]}
      accessible={false}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      {flag}
    </Text>
  );
}

/**
 * The icon for a traffic source badge. Monochrome in the badge's own text
 * colour, decorative (the source is named beside it), and never mirrored:
 * brand marks keep their shape in RTL.
 */
export function TrafficSourceGlyph({
  iconKey,
  color,
  size = 16,
}: {
  iconKey: TrafficSourceIconKey;
  color: string;
  size?: number;
}) {
  if (iconKey === 'share') {
    return (
      <View accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
        <ShareIcon size={size} color={color} />
      </View>
    );
  }
  if (iconKey === 'tabbio') {
    return (
      <View accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
        <TabbioMark size={size - 2} color={color} />
      </View>
    );
  }
  return <HugeIcon icon={TRAFFIC_SOURCE_HUGEICONS[iconKey]} size={size} color={color} decorative />;
}

// ─── Breakdown row (where-from / how-found) ──────────────────────────
export function BreakdownRow({
  code,
  label,
  count,
  ratio,
  active,
  flag,
  sourceIcon,
}: {
  code: string;
  label: string;
  count: string;
  ratio: number;
  active?: boolean;
  /** Country flag shown in the badge instead of the letter code. */
  flag?: string | null;
  /** Traffic-source icon shown in the badge instead of the letter code. */
  sourceIcon?: TrafficSourceIconKey | null;
}) {
  const palette = useAnalyticsPalette();
  const { isRTL } = useLocale();
  const fillColor = active ? BRAND : palette.trackInactiveFill;
  const fillPercent = Math.max(6, Math.min(100, Math.round(ratio * 100)));

  return (
    <View style={[styles.breakdownRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
      <View style={[styles.codeChip, { backgroundColor: palette.chipBg }]}>
        {flag ? (
          <FlagGlyph flag={flag} />
        ) : sourceIcon ? (
          <TrafficSourceGlyph iconKey={sourceIcon} color={palette.chipText} />
        ) : (
          <Text style={[styles.codeChipText, { color: palette.chipText }]} numberOfLines={1}>
            {code}
          </Text>
        )}
      </View>
      <Text
        style={[styles.breakdownName, { color: palette.text, textAlign: isRTL ? 'right' : 'left', writingDirection: 'auto' }]}
        numberOfLines={1}
      >
        {label}
      </Text>
      <View style={[styles.breakdownTrailing, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
        <View style={[styles.barTrack, { backgroundColor: palette.track }]}>
          <View
            style={[
              styles.barFill,
              {
                width: `${fillPercent}%`,
                backgroundColor: fillColor,
                alignSelf: isRTL ? 'flex-end' : 'flex-start',
              },
            ]}
          />
        </View>
        <Text style={[styles.breakdownCount, { color: palette.textStrong }]} numberOfLines={1}>
          {count}
        </Text>
      </View>
    </View>
  );
}

// ─── Recruiter keyword row ───────────────────────────────────────────
/**
 * A keyword recruiters searched with: the term verbatim, its counts line, and
 * the same proportional bar the breakdown rows use. Plain `row` / `left` like
 * RecruiterViewsLine (forced native RTL mirrors it once); the keyword itself
 * keeps automatic direction because a Latin query can sit in the Arabic UI.
 */
export function KeywordRow({
  keyword,
  counts,
  ratio,
  active,
}: {
  keyword: string;
  counts: string;
  ratio: number;
  active?: boolean;
}) {
  const palette = useAnalyticsPalette();
  const fillPercent = Math.max(6, Math.min(100, Math.round(ratio * 100)));
  return (
    <View style={[styles.breakdownRow, styles.keywordRow]}>
      <View style={styles.activityCopy}>
        <Text style={[styles.activityName, styles.keywordText, { color: palette.textStrong }]}>
          {keyword}
        </Text>
        <Text style={[styles.activitySubtitle, styles.keywordCounts, { color: palette.muted }]}>
          {counts}
        </Text>
      </View>
      <View style={[styles.barTrack, { backgroundColor: palette.track }]}>
        <View
          style={[
            styles.barFill,
            {
              width: `${fillPercent}%`,
              backgroundColor: active ? BRAND : palette.trackInactiveFill,
            },
          ]}
        />
      </View>
    </View>
  );
}
const styles = StyleSheet.create({
  recruiterLine: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  recruiterLineIcon: {
    paddingTop: 2,
  },
  recruiterLineText: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 20,
    textAlign: 'left',
  },

  // Keyword copy
  activityCopy: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  activityName: {
    fontSize: 14,
    fontWeight: '600',
    letterSpacing: -0.14,
  },
  activitySubtitle: {
    fontSize: 12,
    fontWeight: '500',
    letterSpacing: -0.12,
  },

  // Breakdown row
  breakdownRow: {
    width: '100%',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 10,
  },
  codeChip: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  flagGlyph: {
    textAlign: 'center',
  },
  codeChipText: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: -0.12,
    textTransform: 'uppercase',
  },
  breakdownName: {
    flex: 1,
    minWidth: 0,
    fontSize: 16,
    fontWeight: '600',
    letterSpacing: -0.16,
    textTransform: 'capitalize',
  },
  keywordRow: {
    flexDirection: 'row',
  },
  keywordText: {
    textAlign: 'left',
    writingDirection: 'auto',
  },
  keywordCounts: {
    textAlign: 'left',
  },
  breakdownTrailing: {
    alignItems: 'center',
    gap: 12,
  },
  barTrack: {
    width: 72,
    height: 6,
    borderRadius: 33,
    overflow: 'hidden',
  },
  barFill: {
    height: 6,
    borderRadius: 44,
  },
  breakdownCount: {
    fontSize: 16,
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
  },
});
